import { useEffect, useState } from 'react';
import type { LeaderboardData } from '../lib/types';
import { t, tp } from '../lib/i18n';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const KNOWN_STATUSES = ['draft', 'active', 'paused', 'ended'];
function statusLabel(status: string): string {
  return KNOWN_STATUSES.includes(status) ? t(`status.${status}`) : status;
}

async function publicFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${BACKEND_URL}${path}`);
  const data = (await res.json()) as { error?: string } & T;
  if (!res.ok) throw new Error(data.error ?? `http_${res.status}`);
  return data as T;
}

export default function PublicLeaderboard() {
  const campaignId = window.location.pathname.replace(/^\/leaderboard\//, '').replace(/\/$/, '');
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [tab, setTab] = useState<'daily' | 'weekly' | 'monthly' | 'overall'>('overall');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) {
      setError(t('public.noCampaignId'));
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const res = await publicFetch<LeaderboardData>(
          `/api/campaigns/${campaignId}/leaderboard`,
        );
        if (!cancelled) {
          setData(res);
          document.title = `${res.campaignName} — ${t('lb.title')}`;
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    }
    load();
    const id = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [campaignId]);

  if (error) {
    return (
      <main style={{ padding: '1.5rem' }}>
        <div className="topbar"><div className="brand">Viral<span>Loop</span></div></div>
        <div className="error" style={{ marginTop: '1rem' }}>{error}</div>
      </main>
    );
  }

  if (!data) {
    return (
      <main style={{ padding: '1.5rem' }}>
        <div className="muted">{t('lb.loading')}</div>
      </main>
    );
  }

  const rows =
    tab === 'overall'
      ? data.periodicLeaderboards.overall.top
      : data.periodicLeaderboards[tab].top;

  const endsAt = new Date(data.endsAt).getTime();
  const secondsRemaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
  const days = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">Viral<span>Loop</span></div>
        <div className="user">{tp('lb.signupsCount', { n: data.totalParticipants })}</div>
      </header>

      <main>
        <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
          <h1 style={{ margin: 0 }}>{data.campaignName}</h1>
          <span className="badge">
            {data.status === 'active'
              ? tp('detail.endsIn', { d: days, h: hours })
              : statusLabel(data.status)}
          </span>
        </div>

        {!data.topNActive && data.minParticipantsForTopN > 0 && (
          <div className="notice" style={{ marginBottom: '1rem' }}>
            {tp('lb.minNeededPublic', { n: data.remainingForTopN })}
          </div>
        )}

        <div className="card">
          <div className="tabs">
            <button className={tab === 'daily' ? 'active' : ''} onClick={() => setTab('daily')}>
              {t('lb.tabDaily')}
            </button>
            <button className={tab === 'weekly' ? 'active' : ''} onClick={() => setTab('weekly')}>
              {t('lb.tabWeekly')}
            </button>
            <button
              className={tab === 'monthly' ? 'active' : ''}
              onClick={() => setTab('monthly')}
            >
              {t('lb.tabMonthly')}
            </button>
            <button className={tab === 'overall' ? 'active' : ''} onClick={() => setTab('overall')}>
              {t('lb.tabOverall')}
            </button>
          </div>

          <table>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>{t('lb.col.name')}</th>
                <th style={{ textAlign: 'right' }}>{t('lb.col.referrals')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={3} className="muted">{t('lb.emptyAll')}</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.rank}>
                  <td>{r.rank}</td>
                  <td>{r.displayName}</td>
                  <td style={{ textAlign: 'right' }}>
                    {r.referralsThisPeriod ?? r.referralCount ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
